import WarpElement from '@warp-ds/elements-core';
import { customElement } from 'lit/decorators.js';
import { css, html } from 'lit';
import { i18n } from '@lingui/core';
import '@warp-ds/elements/components/button';
import { cordSvgSrc } from './consent-illustrations.js';
import { Recommendations } from '../../server/types.js';
import pulse from '@finn-no/pulse-sdk';

// Local type for the TCF API
type WindowWithTcfApi = Window & {
	_tcf_?: {
		getPermissionsSync: (category: string) => '1' | '0' | null;
		subscribe: (category: string, callback: (value: '1' | '0' | null) => void) => () => void;
		showPrivacyManager: () => void;
	};
	__tcfapi?: (
		action: string,
		val: number,
		cb: (tcData: unknown, success: boolean) => void,
		vendors: Array<string>,
		propertyIds: Array<string>,
		specialFeatures: Array<string>,
	) => void;
};

@customElement('request-consent')
export class RequestConsent extends WarpElement {
	static styles = [
		...WarpElement.styles,
		css`
			*,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0}.hover\:underline:hover,.focus\:underline:focus,.active\:underline:active{text-decoration-line:underline}.static{position:static}.max-w-\[624\]{max-width:62.4rem}.mb-24{margin-bottom:2.4rem}.mt-24{margin-top:2.4rem}@media (width>=768px){.md\:place-self-center{place-self:center}.md\:w-fit{width:fit-content}.md\:mt-24{margin-top:2.4rem}}@media (width>=990px){.lg\:text-left{text-align:left}.lg\:inline{display:inline}};
		`,
	];

	showRecommendations = false;
	data: Recommendations | undefined;
	url!: string;
	imgCdnUrl!: string;
	favoriteBaseUrl!: string;
	brand!: string;
	loginId!: string;
	consentPersonalisation!: '';

	static properties = {
		locale: { type: String },
		url: { type: String },
		imgCdnUrl: { type: String },
		favoriteBaseUrl: { type: String },
		brand: { type: String },
		loginId: { type: String },
		consentPersonalisation: { type: String },
	};

	connectedCallback() {
		super.connectedCallback();
		const locale = this.locale || 'en';
		const messages = JSON.parse(this.getAttribute('translations') || '{}');

		i18n.load(locale, messages);
		i18n.activate(locale);

		(window as unknown as WindowWithTcfApi)?._tcf_?.subscribe('CMP:personalisation', async (value) => {
			this.showRecommendations = value === '1';
			if (this.showRecommendations) {
				this.data = await this.fetchRecommendations();
				this.requestUpdate();
			}
		});
	}

	firstUpdated() {
		pulse.trackEvent({
			type: 'View',
			intent: 'View',
			name: 'Personalisation consent',
			object: {
				type: 'UIElement',
				name: 'View frontpage personalisation consent',
				position: 'Frontpage',
			},
			target: {
				type: 'UIElement',
			},
			vertical: {
				name: 'common_pages',
			},
		});
	}

	async fetchRecommendations() {
		const url = `${this.url}/api/recommendations`;
		try {
			const response = await fetch(url);
			if (response.ok) return await response.json();
		} catch (error) {
			console.error(`Error fetching data from ${url}:`, error);
		}
	}

	openCookieSettings() {
		pulse.trackEvent({
			type: 'Click',
			intent: 'Open',
			name: 'Personalisation consent',
			object: {
				type: 'UIElement',
				name: 'Open cookies settings',
				position: 'Frontpage',
			},
			target: {
				type: 'UIElement',
			},
			vertical: {
				name: 'common_pages',
			},
		});
		if ((window as unknown as WindowWithTcfApi)?._tcf_?.showPrivacyManager) {
			(window as unknown as WindowWithTcfApi)?._tcf_?.showPrivacyManager();
		} else {
			console.log('Mock: Click on cookie settings');
		}
	}

	async activatePersonalisation() {
		pulse.trackEvent({
			type: 'Click',
			intent: 'Accept',
			name: 'Personalisation consent',
			object: {
				type: 'UIElement',
				name: 'Accept frontpage personalisation',
				position: 'Frontpage',
			},
			target: {
				type: 'UIElement',
			},
			vertical: {
				name: 'common_pages',
			},
		});
		if ((window as unknown as WindowWithTcfApi)?.__tcfapi) {
			(window as unknown as WindowWithTcfApi).__tcfapi?.(
				'postCustomConsent',
				2,
				() => {},
				[],
				[this.consentPersonalisation],
				[],
			);
		} else {
			this.showRecommendations = true;
			// Create mock data that indicates consent was given but no history exists yet
			this.data = {
				isPersonal: true,
				items: [],
				size: 0,
				fetchMore: [],
			};
			this.requestUpdate();
		}
	}

	render() {
		return this.showRecommendations && this.data?.size == 0
			? html`<no-history
					locale="${this.locale}"
					translations="${this.getAttribute('translations') || '{}'}"
					fromConsentFlow="true"
				></no-history>`
			: this.showRecommendations
				? html`<frontpage-recommendations
						locale="${this.locale}"
						useOneClickConsent="true"
						.data="${this.data || { items: [], fetchMore: [], isPersonal: true, size: 0 }}"
						translations="${this.getAttribute('translations') || '{}'}"
						url="${this.url}"
						imgCdnUrl="${this.imgCdnUrl}"
						favoriteBaseUrl="${this.favoriteBaseUrl}"
						brand="${this.brand}"
						loginId="${this.loginId}"
						fromConsentFlow="true"
					></frontpage-recommendations>`
				: html`
						<section class="flex flex-col">
							<div class="items-center text-center  mt-16 md:mt-24 max-w-[624] mx-auto">
								<img
									alt="${i18n.t({
										id: 'frontpage.request-consent.alt-text.img',
										message: 'Power plug with lightning symbol',
									})}"
									src="${cordSvgSrc}"
									class="mx-auto"
								/>
								<h2 class="mt-24 t3">
									${i18n.t({
										id: 'frontpage.request-consent.title',
										message: 'Personalisation is off',
									})}
								</h2>
								<p class="text-center mb-24">
									${i18n.t({
										id: 'frontpage.request-consent.text',
										message:
											'Let Vend personalise DBA based on your activity. You can withdraw your acceptance and change your settings in ',
									})}
									<button
										class="text s-text-link lg:text-left bg-transparent ease-in-out lg:inline active:underline focus:underline hover:underline"
										@click="${() => this.openCookieSettings()}"
									>
										${i18n.t({
											id: 'frontpage.request-consent.cookie-settings-link.text',
											message: 'cookie settings',
										})}</button
									>.
								</p>
								<div class="text-center mx-auto w-full md:w-fit md:place-self-center">
									<w-button full-width variant="primary" @click="${() => this.activatePersonalisation()}"
										>${i18n.t({
											id: 'frontpage.request-consent.button',
											message: 'Turn on personalisation',
										})}</w-button
									>
								</div>
							</div>
						</section>
					`;
	}
}
